import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ChevronDown, Download, Save, FileText } from "lucide-react";
import { adminFetchBids, adminPatchBid, adminPlanUrl } from "@/lib/api";
import { adminInputCls, adminBtnCls, adminTabCls } from "./shared";

const STATUSES = ["new", "contacted", "quoted", "won", "closed"];
const FILTERS = ["all", ...STATUSES];

interface BidFile {
  original_filename: string;
  storage_path?: string;
  status: string;
  size?: number;
}

interface Bid {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  project_type: string;
  project_category: string;
  project_address: string;
  start_date: string;
  services: string[];
  message: string;
  language: string;
  status: string;
  internal_notes?: string;
  files: BidFile[];
  created_at: string;
}

const statusChip = (s: string) =>
  `rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.15em] ${
    s === "new"
      ? "border-gold/50 bg-gold/10 text-gold"
      : "border-white/15 text-neutral-400"
  }`;

export const BidsTab = ({ onAuthError }: { onAuthError: () => void }) => {
  const [filter, setFilter] = useState("all");
  const [bids, setBids] = useState<Bid[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [downloading, setDownloading] = useState("");

  const load = useCallback(() => {
    setBids(null);
    adminFetchBids(filter)
      .then((r) => setBids(r.data.bids))
      .catch((e) => {
        if (e?.response?.status === 401) onAuthError();
        else {
          toast.error("Failed to load bid requests");
          setBids([]);
        }
      });
  }, [filter, onAuthError]);

  useEffect(() => load(), [load]);

  const patch = async (id: string, payload: Record<string, unknown>) => {
    try {
      await adminPatchBid(id, payload);
      load();
    } catch (err: any) {
      if (err?.response?.status === 401) onAuthError();
      else toast.error("Action failed");
    }
  };

  const downloadPlan = async (f: BidFile) => {
    if (!f.storage_path) return;
    setDownloading(f.storage_path);
    try {
      const res = await fetch(adminPlanUrl(f.storage_path), {
        headers: { Authorization: `Bearer ${localStorage.getItem("r3-admin-token") || ""}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename || "plan";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download file");
    }
    setDownloading("");
  };

  const field = (label: string, value?: string) =>
    value ? (
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">{label}</p>
        <p className="mt-1 text-sm text-neutral-200">{value}</p>
      </div>
    ) : null;

  return (
    <div data-testid="admin-bids-tab">
      <div className="flex flex-wrap gap-2.5">
        {FILTERS.map((s) => (
          <button key={s} data-testid={`admin-bids-filter-${s}`} onClick={() => setFilter(s)} className={adminTabCls(filter === s)}>
            {s}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-5">
        {bids === null ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-gold" />
          </div>
        ) : bids.length === 0 ? (
          <p data-testid="admin-bids-empty" className="border border-dashed border-white/15 p-10 text-center text-sm text-neutral-500">
            No {filter === "all" ? "" : filter + " "}bid requests.
          </p>
        ) : (
          bids.map((b) => {
            const open = openId === b.id;
            return (
              <div key={b.id} data-testid={`admin-bid-card-${b.id}`} className="border border-white/10 bg-charcoal">
                <button
                  data-testid={`admin-bid-toggle-${b.id}`}
                  onClick={() => setOpenId(open ? null : b.id)}
                  className="flex w-full flex-wrap items-center justify-between gap-3 p-5 text-left md:p-6"
                >
                  <div>
                    <p className="font-display text-lg font-black tracking-tight text-white">
                      {b.name}
                      {b.company && <span className="ml-2 text-sm font-bold text-neutral-400">· {b.company}</span>}
                    </p>
                    <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                      {new Date(b.created_at).toLocaleString()} · {b.project_type || "—"} · {b.language === "es" ? "Español" : "English"}
                      {b.files?.length > 0 && ` · ${b.files.length} plan${b.files.length > 1 ? "s" : ""}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={statusChip(b.status)}>{b.status}</span>
                    <ChevronDown className={`h-4 w-4 text-neutral-500 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                  </div>
                </button>

                {open && (
                  <div className="border-t border-white/10 p-5 md:p-6" data-testid={`admin-bid-detail-${b.id}`}>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {field("Phone", b.phone)}
                      {field("Email", b.email)}
                      {field("Project address / location", b.project_address)}
                      {field("Project type", b.project_type)}
                      {field("Category", b.project_category)}
                      {field("Other category details", (b as any).category_other_text)}
                      {field("Other service details", (b as any).service_other_text)}
                      {field("Estimated start date", b.start_date)}
                      {field("Preferred language", b.language === "es" ? "Español" : "English")}
                      {field("Services requested", b.services?.join(", ") || "—")}
                    </div>
                    {b.message && (
                      <div className="mt-5">
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">Project description</p>
                        <p className="mt-1 text-sm leading-relaxed text-neutral-300">{b.message}</p>
                      </div>
                    )}

                    {b.files?.length > 0 && (
                      <div className="mt-5">
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">Uploaded plans / blueprints</p>
                        <ul className="mt-2 space-y-2">
                          {b.files.map((f, i) => (
                            <li key={i} className="flex items-center justify-between border border-white/10 bg-ink px-4 py-3 text-sm text-neutral-300">
                              <span className="flex items-center gap-2 truncate pr-3">
                                <FileText className="h-4 w-4 shrink-0 text-gold" />
                                <span className="truncate">{f.original_filename}</span>
                                {f.status !== "stored" && <span className="text-xs text-red-400">({f.status})</span>}
                              </span>
                              {f.storage_path && (
                                <button
                                  data-testid={`admin-plan-download-${b.id}-${i}`}
                                  onClick={() => downloadPlan(f)}
                                  disabled={downloading === f.storage_path}
                                  className={adminBtnCls}
                                >
                                  {downloading === f.storage_path ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Download className="h-3.5 w-3.5" />
                                  )}
                                  Download
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="mt-6">
                      <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">Status</p>
                      <div className="flex flex-wrap gap-2.5">
                        {STATUSES.map((s) => (
                          <button
                            key={s}
                            data-testid={`admin-bid-status-${b.id}-${s}`}
                            onClick={() => patch(b.id, { status: s })}
                            className={adminTabCls(b.status === s)}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-6">
                      <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
                        Internal notes (admin only — never shown to customers)
                      </p>
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <textarea
                          data-testid={`admin-bid-notes-${b.id}`}
                          rows={2}
                          defaultValue={b.internal_notes || ""}
                          onChange={(e) => setNotes((n) => ({ ...n, [b.id]: e.target.value }))}
                          className={`${adminInputCls} resize-none`}
                          placeholder="Private notes about this bid…"
                        />
                        <button
                          data-testid={`admin-bid-notes-save-${b.id}`}
                          onClick={() => patch(b.id, { internal_notes: notes[b.id] ?? b.internal_notes ?? "" })}
                          className={`${adminBtnCls} shrink-0 self-start sm:self-center`}
                        >
                          <Save className="h-3.5 w-3.5" /> Save note
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
